import React from 'react'
import { ReactComponent as ChevronLeftIcon } from '@assets/images/Icons/Chevron_left.svg'
import type { BreadcrumbItem, BreadcrumbsProps } from './Breadcrumbs.types'
import { getBreadcrumbRenderables } from './Breadcrumbs.types'
import './Breadcrumbs.css'

const isCurrentItem = (index: number, totalItems: number): boolean =>
  index === totalItems - 1

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  showBackIcon = true,
  onBackClick,
  maxItems,
  darkMode = false,
  className = '',
  style,
}) => {
  const renderables = getBreadcrumbRenderables(items, maxItems)

  const handleBackClick = (): void => {
    if (onBackClick) {
      onBackClick()
      return
    }

    if (items.length >= 2) {
      items[items.length - 2]?.onClick?.()
    }
  }

  const renderItemContent = (
    item: BreadcrumbItem,
    index: number,
    isCurrent: boolean,
  ): React.ReactNode => {
    if (isCurrent) {
      return (
        <span className="ds-breadcrumbs__current" aria-current="page">
          {item.label}
        </span>
      )
    }

    if (item.href) {
      return (
        <a className="ds-breadcrumbs__link" href={item.href}>
          {item.label}
        </a>
      )
    }

    if (item.onClick) {
      return (
        <button
          type="button"
          className="ds-breadcrumbs__link ds-breadcrumbs__link--button"
          onClick={item.onClick}
        >
          {item.label}
        </button>
      )
    }

    return <span className="ds-breadcrumbs__link">{item.label}</span>
  }

  return (
    <nav
      className={`ds-breadcrumbs${darkMode ? ' ds-breadcrumbs--dark' : ''} ${className}`.trim()}
      style={style}
      aria-label="Breadcrumb"
    >
      {showBackIcon && (
        <button
          type="button"
          className="ds-breadcrumbs__back"
          aria-label="Go back"
          onClick={handleBackClick}
        >
          <ChevronLeftIcon width={16} height={16} aria-hidden="true" />
        </button>
      )}

      <ol className="ds-breadcrumbs__list">
        {renderables.map((renderable, renderIndex) => {
          if (renderable.kind === 'ellipsis') {
            return (
              <li
                key="ellipsis"
                className="ds-breadcrumbs__segment"
                aria-hidden="true"
              >
                <span className="ds-breadcrumbs__separator">/</span>
                <span className="ds-breadcrumbs__ellipsis">...</span>
              </li>
            )
          }

          const { item, index } = renderable
          const isCurrent = isCurrentItem(index, items.length)
          const key = item.id ?? `${item.label}-${index}`

          return (
            <li key={key} className="ds-breadcrumbs__segment">
              {renderIndex > 0 && (
                <span className="ds-breadcrumbs__separator" aria-hidden="true">
                  /
                </span>
              )}
              {renderItemContent(item, index, isCurrent)}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export default Breadcrumbs
